/**
 * Library Service — Digital Sports Library API
 *
 * Central service for the VUKZ digital library.
 * All calls go through the AZURE client (which rewrites /library/ → /arena/library/).
 *
 * Backend endpoints (routes_library.py):
 *   GET    /api/library/items                    — List items (filters: item_type, sport, status, evidence_level)
 *   GET    /api/library/items/:id                — Item detail
 *   POST   /api/library/items                    — Create item (DRAFT)
 *   PUT    /api/library/items/:id                — Update item
 *   POST   /api/library/items/:id/submit         — Submit for review
 *   POST   /api/library/items/:id/approve        — Approve (GESTOR)
 *   POST   /api/library/items/:id/reject         — Reject (GESTOR)
 *   POST   /api/library/items/:id/archive        — Archive
 *   POST   /api/library/search                   — AI Research (Researcher agent)
 *   GET    /api/library/methodologies             — List methodologies
 *   GET    /api/library/methodologies/:id         — Methodology detail
 *   GET    /api/library/methodologies/:id/templates — Templates for methodology
 */
import { AZURE } from './api';

// ─── Cache ───────────────────────────────────────────────────────────
let _statsCache = null;
let _statsCacheTs = null;
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes

// ─── Items CRUD ──────────────────────────────────────────────────────

/**
 * Fetch library items with optional filters.
 * @param {Object} filters — { item_type, sport, status, evidence_level, q }
 * @returns {Promise<Array>}
 */
export async function getLibraryItems(filters = {}) {
  try {
    const params = {};
    if (filters.item_type) params.item_type = filters.item_type;
    if (filters.sport)     params.sport = filters.sport;
    if (filters.status)    params.status = filters.status;
    if (filters.evidence_level) params.evidence_level = filters.evidence_level;
    if (filters.q)         params.q = filters.q;

    const { data } = await AZURE.get('/library/items', { params });
    return data.data || data.items || [];
  } catch (error) {
    console.error('[LibraryService] getLibraryItems error:', error);
    throw error;
  }
}

/**
 * Fetch a single library item by ID.
 * @param {number|string} itemId
 * @returns {Promise<Object|null>}
 */
export async function getLibraryItem(itemId) {
  try {
    const { data } = await AZURE.get(`/library/items/${itemId}`);
    return data.data || null;
  } catch (error) {
    console.error('[LibraryService] getLibraryItem error:', error);
    throw error;
  }
}

/**
 * Create a new library item (starts as DRAFT).
 * @param {Object} payload — { title, item_type, sport, category, description, content_json, tags_json }
 * @returns {Promise<Object>}
 */
export async function createLibraryItem(payload) {
  try {
    const { data } = await AZURE.post('/library/items', payload);
    _invalidateStatsCache();
    return data;
  } catch (error) {
    console.error('[LibraryService] createLibraryItem error:', error);
    throw error;
  }
}

/**
 * Update an existing library item.
 * @param {number|string} itemId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function updateLibraryItem(itemId, payload) {
  try {
    const { data } = await AZURE.put(`/library/items/${itemId}`, payload);
    return data;
  } catch (error) {
    console.error('[LibraryService] updateLibraryItem error:', error);
    throw error;
  }
}

// ─── Workflow Actions ────────────────────────────────────────────────

export async function submitForReview(itemId) {
  try {
    const { data } = await AZURE.post(`/library/items/${itemId}/submit`);
    _invalidateStatsCache();
    return data;
  } catch (error) {
    console.error('[LibraryService] submitForReview error:', error);
    throw error;
  }
}

export async function approveItem(itemId) {
  try {
    const { data } = await AZURE.post(`/library/items/${itemId}/approve`);
    _invalidateStatsCache();
    return data;
  } catch (error) {
    console.error('[LibraryService] approveItem error:', error);
    throw error;
  }
}

export async function rejectItem(itemId, reason) {
  try {
    const { data } = await AZURE.post(`/library/items/${itemId}/reject`, {
      rejection_reason: reason,
    });
    _invalidateStatsCache();
    return data;
  } catch (error) {
    console.error('[LibraryService] rejectItem error:', error);
    throw error;
  }
}

export async function archiveItem(itemId) {
  try {
    const { data } = await AZURE.post(`/library/items/${itemId}/archive`);
    _invalidateStatsCache();
    return data;
  } catch (error) {
    console.error('[LibraryService] archiveItem error:', error);
    throw error;
  }
}

// ─── AI Research ─────────────────────────────────────────────────────

/**
 * Trigger the Researcher Agent to find academic content.
 * @param {string} query — Natural language search query
 * @param {string} sport — 'swimming' | 'football' | etc.
 * @returns {Promise<Object>}
 */
export async function researchTopic(query, sport = 'swimming') {
  try {
    const { data } = await AZURE.post('/library/search', { query, sport });
    return data;
  } catch (error) {
    console.error('[LibraryService] researchTopic error:', error);
    throw error;
  }
}

// ─── Methodologies ───────────────────────────────────────────────────

export async function getMethodologies(filters = {}) {
  try {
    const { data } = await AZURE.get('/library/methodologies', { params: filters });
    return data.data?.methodologies || [];
  } catch (error) {
    console.error('[LibraryService] getMethodologies error:', error);
    throw error;
  }
}

export async function getMethodologyDetail(methodologyId) {
  try {
    const { data } = await AZURE.get(`/library/methodologies/${methodologyId}`);
    return data.data || null;
  } catch (error) {
    console.error('[LibraryService] getMethodologyDetail error:', error);
    throw error;
  }
}

// ─── Aggregated Stats (for Hub) ──────────────────────────────────────

/**
 * Compute stats from the items list.
 * Uses a short-lived cache to avoid redundant API calls.
 * @returns {Promise<Object>} — { total, byType, byStatus, bySport, byEvidence }
 */
export async function getLibraryStats() {
  const now = Date.now();
  if (_statsCache && _statsCacheTs && (now - _statsCacheTs < CACHE_TTL)) {
    return _statsCache;
  }

  const items = await getLibraryItems();

  const stats = {
    total: items.length,
    byType: {},
    byStatus: {},
    bySport: {},
    byEvidence: {},
  };

  items.forEach((item) => {
    // By type
    const t = item.item_type || 'unknown';
    stats.byType[t] = (stats.byType[t] || 0) + 1;

    // By status
    const s = item.status || 'unknown';
    stats.byStatus[s] = (stats.byStatus[s] || 0) + 1;

    // By sport
    const sp = item.sport || 'unknown';
    stats.bySport[sp] = (stats.bySport[sp] || 0) + 1;

    // By evidence
    const e = item.evidence_level || 'unknown';
    stats.byEvidence[e] = (stats.byEvidence[e] || 0) + 1;
  });

  _statsCache = stats;
  _statsCacheTs = now;
  return stats;
}

function _invalidateStatsCache() {
  _statsCache = null;
  _statsCacheTs = null;
}

// ─── Constants & Helpers ─────────────────────────────────────────────

/** Item type metadata — icon, label, description, color */
export const ITEM_TYPES = {
  protocol:    { key: 'protocol',    label: 'Protocolo',    icon: 'clipboard-list', description: 'Rotinas de treino estruturadas',       color: '#111111' },
  drill:       { key: 'drill',       label: 'Drill',        icon: 'zap',            description: 'Exercícios práticos e específicos',   color: '#374151' },
  benchmark:   { key: 'benchmark',   label: 'Benchmark',    icon: 'bar-chart-2',    description: 'Referências de desempenho',           color: '#6b7280' },
  methodology: { key: 'methodology', label: 'Metodologia',  icon: 'book-open',      description: 'Modelos de periodização e treino',    color: '#111111' },
  session:     { key: 'session',     label: 'Sessão',       icon: 'calendar',       description: 'Planos de sessão completos',          color: '#374151' },
  microciclo:  { key: 'microciclo',  label: 'Microciclo',   icon: 'repeat',         description: 'Ciclos semanais de treinamento',      color: '#6b7280' },
  checklist:   { key: 'checklist',   label: 'Checklist',    icon: 'check-square',   description: 'Listas de verificação para eventos',  color: '#111111' },
  decision:    { key: 'decision',    label: 'Decisão',      icon: 'git-branch',     description: 'Árvores de decisão tática',           color: '#374151' },
};

/** Status metadata */
export const ITEM_STATUSES = {
  draft:          { label: 'Rascunho',  dotColor: '#9ca3af' },
  pending_review: { label: 'Em Revisão', dotColor: '#f59e0b' },
  approved:       { label: 'Aprovado',  dotColor: '#16a34a' },
  archived:       { label: 'Arquivado', dotColor: '#6b7280' },
};

/** Evidence level metadata */
export const EVIDENCE_LEVELS = {
  A:               { label: 'Nível A',           badge: 'A',  color: '#111111' },
  B:               { label: 'Nível B',           badge: 'B',  color: '#374151' },
  C:               { label: 'Nível C',           badge: 'C',  color: '#6b7280' },
  expert_opinion:  { label: 'Opinião Especialista', badge: 'E', color: '#9ca3af' },
  anecdotal:       { label: 'Anedótico',         badge: '?',  color: '#d1d5db' },
};

/** Sport labels (pt-BR) */
export const SPORT_LABELS = {
  swimming:           'Natação',
  football:           'Futebol',
  futebol_americano:  'Futebol Americano',
  triathlon:          'Triathlon',
  athletics:          'Atletismo',
  judo:               'Judô',
  multi:              'Multi-esporte',
};

/** Sport icon keys (use lucide-react icons in components) */
export const SPORT_ICONS = {
  swimming:           'waves',
  football:           'goal',
  futebol_americano:  'trophy',
  triathlon:          'activity',
  athletics:          'activity',
  judo:               'shield',
  multi:              'layers',
};
